import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { getDashboardMetrics, getRevenueChart, getOccupancyChart, seedData, recalculatePrices } from "../lib/api";
import { toast } from "sonner";
import axios from "axios";
import { useYear } from "../context/YearContext";
import YearSwitcher from "../components/YearSwitcher";
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Banknote, 
  BarChart3,
  RefreshCw,
  Database,
  Cloud,
  CloudOff,
  CheckCircle2,
  AlertCircle,
  Clock,
  Loader2,
  Target,
  Flag,
  Download
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  ReferenceLine,
} from "recharts";
import RMAEffectiveness from "../components/RMAEffectiveness";

const MetricCard = ({ title, value, change, icon: Icon, prefix = "", suffix = "", positive = true }) => (
  <Card className="metric-card card-hover" data-testid={`metric-${title.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <p className="text-xs sm:text-sm font-medium text-muted-foreground truncate">{title}</p>
        <p className="text-xl sm:text-3xl font-bold tracking-tight mt-1 sm:mt-2 truncate">
          {prefix}{typeof value === "number" ? value.toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : value}{suffix}
        </p>
        {change !== undefined && (
          <div className={`flex items-center gap-1 mt-1 sm:mt-2 text-xs sm:text-sm ${change >= 0 ? "status-positive" : "status-negative"}`}>
            {change >= 0 ? <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" /> : <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4" />}
            <span>{change >= 0 ? "+" : ""}{change}%</span>
          </div>
        )}
      </div>
      <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 sm:w-6 sm:h-6 text-primary" />
      </div>
    </div>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
        <p className="text-sm font-medium">{label}</p>
        {payload.map((entry) => (
          <p key={`tooltip-${entry.name}-${entry.value}`} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: €{entry.value?.toLocaleString("es-ES")}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const Dashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [occupancyData, setOccupancyData] = useState([]);
  const [trendsData, setTrendsData] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [goalData, setGoalData] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [downloadingGoalPdf, setDownloadingGoalPdf] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;
  const { year } = useYear();
  const getAuthHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }), []);

  const downloadGoalPdf = async () => {
    setDownloadingGoalPdf(true);
    try {
      const res = await axios.get(`${API}/api/reports/revenue-goal/pdf?year=${year}`, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `objetivo_revenue_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (e) {
      toast.error("Error al descargar el PDF");
    } finally {
      setDownloadingGoalPdf(false);
    }
  };

  const fetchGoal = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [goalRes, schedRes] = await Promise.all([
        axios.get(`${API}/api/analytics/revenue-goal?year=${year}`, { headers }),
        axios.get(`${API}/api/analytics/scheduler-status`, { headers }),
      ]);
      setGoalData(goalRes.data);
      setSchedulerStatus(schedRes.data);
    } catch (e) {
      console.log("Goal/scheduler data not available:", e.message);
    }
  }, [API, getAuthHeaders, year]);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [oscarRes, summaryRes] = await Promise.all([
        axios.get(`${API}/api/analytics/oscar-sync-status`, { headers }),
        axios.get(`${API}/api/analytics/oscar-status-summary`, { headers })
      ]);

      setSyncStatus({
        ...oscarRes.data,
        summary: summaryRes.data,
        lastDataUpdate: summaryRes.data?.last_api_sync || null,
        hasRecentData: summaryRes.data?.health === "healthy"
      });
    } catch (e) {
      console.log("Sync status not available:", e.message);
    }
  }, [API, getAuthHeaders]);

  const fetchData = useCallback(async () => {
    try {
      const [metricsRes, revenueRes, occupancyRes] = await Promise.all([
        getDashboardMetrics(),
        getRevenueChart(),
        getOccupancyChart()
      ]);
      setMetrics(metricsRes.data);
      setRevenueData(revenueRes.data);
      setOccupancyData(occupancyRes.data);
      
      // Fetch sync status
      await fetchSyncStatus();
      // Fetch revenue goal
      await fetchGoal();
      
      // Fetch trends separately (uses auth interceptor)
      try {
        const headers = getAuthHeaders();
        const trendsRes = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/dashboard/trends-30days`,
          { headers }
        );
        if (trendsRes.data?.success) {
          setTrendsData(trendsRes.data);
        }
      } catch (e) {
        console.log("Trends data not available:", e.message);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      if (error.response?.status === 401) {
        toast.error("Sesión expirada");
      }
    } finally {
      setLoading(false);
    }
  }, [fetchSyncStatus, fetchGoal, getAuthHeaders]);

  const handleSyncOscar = async () => {
    setSyncing(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(
        `${API}/api/analytics/sync-oscar-billing`,
        {},
        { headers, timeout: 180000 }
      );
      const data = res.data || {};

      // Show all positive messages
      if (Array.isArray(data.messages)) {
        data.messages.forEach((msg) => toast.success(msg));
      }
      // Show warnings (non-fatal issues like 2FA blocked)
      if (Array.isArray(data.warnings) && data.warnings.length > 0) {
        data.warnings.forEach((warn) => toast.warning(warn, { duration: 7000 }));
      }

      if (data.success) {
        if (!data.messages || data.messages.length === 0) {
          toast.success("Sincronización completada");
        }
        await fetchSyncStatus();
        await fetchData();
      } else if (!data.warnings || data.warnings.length === 0) {
        toast.error(data.error || "Error al sincronizar");
      }
    } catch (error) {
      const errMsg = error.response?.data?.error || error.response?.data?.detail || error.message;
      toast.error(`Error al sincronizar con Oscar: ${errMsg}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleSeedData = async () => {
    try {
      await seedData();
      toast.success("Datos iniciales cargados");
      fetchData();
    } catch (error) {
      if (error.response?.data?.message === "Data already seeded") {
        toast.info("Los datos ya han sido cargados");
      } else {
        toast.error("Error al cargar datos");
      }
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await recalculatePrices();
      await fetchData();
      toast.success("Precios recalculados");
    } catch (error) {
      toast.error("Error al recalcular precios");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const occupancyColors = ["#10B981", "#3B82F6", "#8B5CF6", "#F59E0B", "#EF4444", "#EC4899", "#6366F1"];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">Resumen de rendimiento del hotel</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <YearSwitcher />
          <Button variant="outline" onClick={handleSeedData} data-testid="seed-data-btn">
            <Database className="w-4 h-4 mr-2" />
            Cargar Datos
          </Button>
          <Button onClick={handleRefresh} disabled={refreshing} data-testid="refresh-prices-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Recalcular
          </Button>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="dashboard-grid">
        <MetricCard
          title="RevPAR"
          value={metrics?.revpar || 0}
          change={metrics?.revenue_change}
          icon={Banknote}
          prefix="€"
        />
        <MetricCard
          title="ADR (Media)"
          value={metrics?.adr || 0}
          icon={TrendingUp}
          prefix="€"
        />
        <MetricCard
          title="Ocupación Hoy"
          value={metrics?.occupancy_rate || 0}
          change={metrics?.occupancy_change}
          icon={Users}
          suffix="%"
        />
        <MetricCard
          title="Producción Hoy"
          value={metrics?.total_revenue || 0}
          change={metrics?.revenue_change}
          icon={BarChart3}
          prefix="€"
        />
      </div>

      {/* Revenue Goal Widget - Camino a €1.9M */}
      {goalData && (
        <Card data-testid="revenue-goal-card" className="border-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                <CardTitle className="text-lg">Camino a €{(goalData.production.goal / 1_000_000).toFixed(1)}M</CardTitle>
              </div>
              <span
                className="text-xs px-3 py-1 rounded-full font-semibold"
                style={{
                  backgroundColor:
                    goalData.production.status_color === "green" ? "#dcfce7"
                    : goalData.production.status_color === "yellow" ? "#fef3c7" : "#fee2e2",
                  color:
                    goalData.production.status_color === "green" ? "#15803d"
                    : goalData.production.status_color === "yellow" ? "#a16207" : "#b91c1c",
                }}
                data-testid="goal-status-label"
              >
                {goalData.production.status_label}
              </span>
            </div>
            <CardDescription>
              Producción {goalData.current_year} (Ene-Dic): <span className="font-semibold text-foreground">€{goalData.production.projected_full_year.toLocaleString("es-ES")}</span>
              <span className="ml-2 text-xs">
                ({goalData.production.projected_full_year > goalData.production.previous_year_total ? "+" : ""}
                {goalData.production.previous_year_total > 0
                  ? (((goalData.production.projected_full_year - goalData.production.previous_year_total) / goalData.production.previous_year_total) * 100).toFixed(1)
                  : 0}% vs {goalData.current_year - 1})
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Progress bar */}
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">Progreso anual</span>
                <span className="font-bold" data-testid="goal-progress-percent">
                  {goalData.production.progress_percent}%
                </span>
              </div>
              <div className="h-3 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(100, goalData.production.progress_percent)}%`,
                    backgroundColor:
                      goalData.production.status_color === "green" ? "#10b981"
                      : goalData.production.status_color === "yellow" ? "#f59e0b" : "#ef4444",
                  }}
                />
              </div>
            </div>

            {/* Production + Billing side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Production stats */}
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Producción</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Proyección anual</span>
                    <span className="font-semibold">€{goalData.production.projected_full_year.toLocaleString("es-ES")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Brecha al objetivo</span>
                    <span className="font-bold text-red-600" data-testid="goal-gap">
                      €{goalData.production.gap_to_goal.toLocaleString("es-ES")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Necesario/día</span>
                    <span className="font-semibold">€{goalData.production.daily_needed_to_reach_goal.toLocaleString("es-ES")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ritmo actual/día</span>
                    <span className={`font-semibold ${
                      goalData.production.daily_pace_current >= goalData.production.daily_needed_to_reach_goal
                        ? "text-green-600" : "text-orange-600"
                    }`}>
                      €{goalData.production.daily_pace_current.toLocaleString("es-ES")}
                      {goalData.production.daily_pace_current >= goalData.production.daily_needed_to_reach_goal ? " ✓" : ""}
                    </span>
                  </div>
                </div>
              </div>

              {/* Billing stats */}
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-2" data-testid="goal-billing-target">Facturación · objetivo €{(goalData.billing.goal / 1_000_000).toFixed(1)}M</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">YTD</span>
                    <span className="font-semibold">€{goalData.billing.ytd.toLocaleString("es-ES")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Proyección anual</span>
                    <span className="font-semibold">€{goalData.billing.projected_full_year.toLocaleString("es-ES")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Año anterior total</span>
                    <span className="font-semibold">€{goalData.billing.previous_year_total.toLocaleString("es-ES")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Progreso</span>
                    <span className="font-bold" style={{
                      color: goalData.billing.status_color === "green" ? "#15803d"
                      : goalData.billing.status_color === "yellow" ? "#a16207" : "#b91c1c"
                    }}>
                      {goalData.billing.progress_percent}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={downloadGoalPdf}
                disabled={downloadingGoalPdf}
                data-testid="goal-download-pdf-btn"
              >
                {downloadingGoalPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Descargar PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.href = "/forecast"}
                data-testid="goal-see-suggestions-btn"
              >
                <Flag className="w-4 h-4 mr-2" />
                Ver sugerencias IA →
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* RMA Effectiveness Tracker */}
      <RMAEffectiveness />

      {/* Sync Status Panel - Estado Oscar */}
      <Card className="border-2 border-dashed" data-testid="oscar-status-card">
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                syncStatus?.summary?.health_color === "green"
                  ? "bg-green-100 text-green-600"
                  : syncStatus?.summary?.health_color === "yellow"
                  ? "bg-yellow-100 text-yellow-600"
                  : "bg-red-100 text-red-600"
              }`}>
                {syncStatus?.is_syncing ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : syncStatus?.summary?.health === "healthy" ? (
                  <Cloud className="w-6 h-6" />
                ) : (
                  <CloudOff className="w-6 h-6" />
                )}
              </div>
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  Estado Oscar PMS
                  {syncStatus?.summary?.health === "healthy" ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500" data-testid="oscar-status-icon-healthy" />
                  ) : (
                    <AlertCircle className={`w-4 h-4 ${
                      syncStatus?.summary?.health_color === "yellow" ? "text-yellow-500" : "text-red-500"
                    }`} data-testid="oscar-status-icon-warning" />
                  )}
                </h3>
                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mt-1">
                  <span className="flex items-center gap-1" data-testid="oscar-last-sync">
                    <Clock className="w-3 h-3" />
                    {syncStatus?.summary?.health_label || "Sin sincronizar aún"}
                  </span>
                  {syncStatus?.summary && (
                    <span
                      className="text-xs px-2 py-0.5 rounded font-medium"
                      style={{
                        backgroundColor:
                          syncStatus.summary.health_color === "green"
                            ? "#dcfce7"
                            : syncStatus.summary.health_color === "yellow"
                            ? "#fef9c3"
                            : "#fee2e2",
                        color:
                          syncStatus.summary.health_color === "green"
                            ? "#15803d"
                            : syncStatus.summary.health_color === "yellow"
                            ? "#a16207"
                            : "#b91c1c",
                      }}
                      data-testid="oscar-live-data-badge"
                    >
                      {syncStatus.summary.live_price_percent}% datos en vivo
                      {" · "}
                      {syncStatus.summary.rooms_with_live_prices}/{syncStatus.summary.total_rooms_count} hab.
                    </span>
                  )}
                </div>
                {syncStatus?.summary?.messages && syncStatus.summary.messages.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1" data-testid="oscar-status-message">
                    {syncStatus.summary.messages[0]}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.open("https://oscar.redforts.com", "_blank")}
                data-testid="open-oscar-btn"
              >
                Abrir Oscar
              </Button>
              <Button 
                size="sm"
                onClick={handleSyncOscar}
                disabled={syncing || !syncStatus?.configured}
                data-testid="sync-oscar-btn"
              >
                {syncing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sincronizando...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Sincronizar
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Automation Status - Scheduler Jobs */}
      {schedulerStatus && schedulerStatus.scheduler_running && (
        <Card data-testid="automation-status-card" className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center">
                  <Clock className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm flex items-center gap-2">
                    Automatización diaria activa
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {schedulerStatus.total_jobs} tareas programadas · Sync diaria 9:00 · Email diario 9:30
                  </p>
                </div>
              </div>
              <div className="hidden lg:flex items-center gap-1.5 text-xs flex-wrap">
                {schedulerStatus.jobs
                  .filter(j => j.id !== "auto_sync" && j.id !== "golden_rule_18h")
                  .slice(0, 5)
                  .map((job) => {
                    const labelMap = {
                      monthly_stats_sync: "9:00 Sync",
                      oscar_billing_sync: "9:05 Facturas",
                      oscar_live_sync: "9:10 Ocupación",
                      ai_suggestions_cache: "9:15 IA",
                      smart_alerts_check: "9:25 Alertas",
                      daily_summary_email: "9:30 Email",
                    };
                    return (
                      <span
                        key={job.id}
                        className="px-2 py-1 rounded bg-white text-indigo-700 font-medium border border-indigo-100"
                        data-testid={`job-${job.id}`}
                      >
                        {labelMap[job.id] || job.name}
                      </span>
                    );
                  })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <Card className="chart-container" data-testid="revenue-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Ingresos vs Proyección</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData.slice(-14)}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorProjected" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="date" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#64748B" }}
                    tickFormatter={(value) => value.split("-").slice(1).join("/")}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#64748B" }}
                    tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    name="Ingresos"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    fill="url(#colorRevenue)"
                  />
                  <Area
                    type="monotone"
                    dataKey="projected"
                    name="Proyección"
                    stroke="#10B981"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    fill="url(#colorProjected)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Occupancy Chart */}
        <Card className="chart-container" data-testid="occupancy-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Ocupación por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={occupancyData} layout="vertical">
                  <XAxis 
                    type="number" 
                    domain={[0, 100]}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#64748B" }}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: "#64748B" }}
                    width={120}
                  />
                  <Tooltip
                    formatter={(value) => [`${value}%`, "Ocupación"]}
                    contentStyle={{
                      backgroundColor: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar dataKey="occupancy" radius={[0, 4, 4, 0]}>
                    {occupancyData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={occupancyColors[index % occupancyColors.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <Card data-testid="quick-stats">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Resumen de Habitaciones</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-muted rounded-lg">
              <p className="text-2xl font-bold">{metrics?.total_rooms || 0}</p>
              <p className="text-sm text-muted-foreground">Total Habitaciones</p>
            </div>
            <div className="text-center p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{metrics?.occupied_rooms || 0}</p>
              <p className="text-sm text-muted-foreground">Ocupadas</p>
            </div>
            <div className="text-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{(metrics?.total_rooms || 0) - (metrics?.occupied_rooms || 0)}</p>
              <p className="text-sm text-muted-foreground">Disponibles</p>
            </div>
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{metrics?.occupancy_rate?.toFixed(1) || 0}%</p>
              <p className="text-sm text-muted-foreground">Tasa Ocupación</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Trends Chart - 30 Days */}
      {trendsData && (
        <Card data-testid="trends-chart">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                  Tendencia de Ocupación (30 días)
                </CardTitle>
                <CardDescription>
                  Promedio: {trendsData.summary?.average_occupancy}% | 
                  Máx: {trendsData.summary?.max_occupancy}% | 
                  Mín: {trendsData.summary?.min_occupancy}%
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendsData.trends}>
                  <defs>
                    <linearGradient id="occupancyGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="date_short" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: "#64748B" }}
                    interval={4}
                  />
                  <YAxis 
                    domain={[0, 100]}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: "#64748B" }}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip
                    formatter={(value, name) => [`${value}%`, "Ocupación"]}
                    labelFormatter={(label, payload) => {
                      if (payload && payload[0]) {
                        return `${payload[0].payload.day_name} ${label}`;
                      }
                      return label;
                    }}
                    contentStyle={{
                      backgroundColor: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="occupancy_rate" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    fill="url(#occupancyGradient)"
                  />
                  <ReferenceLine y={70} stroke="#f59e0b" strokeDasharray="5 5" />
                  <ReferenceLine y={90} stroke="#dc2626" strokeDasharray="5 5" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-4 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-3 h-0.5 bg-[#f59e0b]"></span> 70% (Media-Alta)
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-0.5 bg-[#dc2626]"></span> 90% (Muy Alta)
              </span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
